import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from '../../i18n/useTranslation'
import { useAuth } from '../../contexts/AuthContext'
import { useTheme } from '../../hooks/useTheme'
import { usePopup } from '../../hooks/usePopup'
import { supportApi, type SupportReportCategory } from '../../api'
import type { ApiError } from '../../api'
import { Button } from '../ui'

const MAX_MESSAGE_LENGTH = 2000

export default function SupportReportPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const theme = useTheme()
  const { showSuccess, showError } = usePopup()
  const location = useLocation()

  const hasUsername = Boolean(user?.username?.trim())
  const fromPath =
    location.state && typeof location.state === 'object' && 'fromPath' in location.state
      ? String((location.state as { fromPath?: string }).fromPath ?? '')
      : ''
  const [category, setCategory] = useState<SupportReportCategory>('BUG')
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  const canSubmit =
    hasUsername && message.trim().length > 0 && message.trim().length <= MAX_MESSAGE_LENGTH && !submitting

  async function handleSubmit() {
    if (!canSubmit) return
    try {
      setSubmitting(true)
      await supportApi.submitReport({
        category,
        message: message.trim(),
        path: fromPath || location.pathname || null,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
        appVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? null,
      })
      setSubmitted(true)
      setMessage('')
      showSuccess(t.support.success)
    } catch (err: unknown) {
      const apiError = err as ApiError
      if (apiError?.status === 429) {
        showError(t.support.errors.rateLimited)
      } else if (apiError?.status === 400) {
        showError(apiError.message || t.support.errors.validation)
      } else {
        showError(t.support.errors.submitFailed)
      }
    } finally {
      setSubmitting(false)
    }
  }

  const categories: { value: SupportReportCategory; label: string; description: string }[] = [
    {
      value: 'BUG',
      label: t.support.categories.bug.label,
      description: t.support.categories.bug.description,
    },
    {
      value: 'BLOCKING',
      label: t.support.categories.blocking.label,
      description: t.support.categories.blocking.description,
    },
  ]

  return (
    <div className="p-5">
      <header className="mb-6">
        <h1 className="text-3xl font-bold mb-2">{t.support.title}</h1>
        <p className="text-sm opacity-70">{t.support.subtitle}</p>
      </header>

      {!hasUsername && (
        <div
          className="mb-5 rounded-xl p-4"
          style={{
            background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12) 0%, rgba(217, 119, 6, 0.08) 100%)',
            border: '1.5px solid rgba(245, 158, 11, 0.3)',
          }}
        >
          <div className="font-semibold mb-2" style={{ color: 'rgba(245, 158, 11, 0.95)' }}>
            {t.support.usernameMissing.title}
          </div>
          <p className="text-sm opacity-90 leading-relaxed mb-2">
            {t.support.usernameMissing.message}
          </p>
          <p className="text-sm opacity-90 leading-relaxed">
            {t.support.usernameMissing.details}
          </p>
        </div>
      )}

      {submitted && hasUsername && (
        <div
          className="mb-5 rounded-xl p-4 text-sm"
          style={{
            backgroundColor: 'rgba(34, 197, 94, 0.12)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            color: theme.text.primary,
          }}
        >
          {t.support.successHint}
        </div>
      )}

      <section className="mb-5 space-y-3">
        <div className="text-xs font-bold uppercase tracking-widest opacity-60">
          {t.support.categoryLabel}
        </div>
        {categories.map((item) => {
          const selected = category === item.value
          return (
            <button
              key={item.value}
              type="button"
              disabled={!hasUsername}
              onClick={() => setCategory(item.value)}
              className="w-full rounded-2xl p-4 text-left transition-transform active:scale-[0.99]"
              style={{
                backgroundColor: theme.cardBgLight,
                border: selected
                  ? `2px solid ${theme.accent.text}`
                  : `1px solid ${theme.cardBorder}`,
                opacity: hasUsername ? 1 : 0.55,
                cursor: hasUsername ? 'pointer' : 'not-allowed',
              }}
            >
              <div className="font-semibold" style={{ color: theme.text.primary }}>
                {item.label}
              </div>
              <div className="text-sm opacity-70 mt-1">{item.description}</div>
            </button>
          )
        })}
      </section>

      <section className="mb-6">
        <label className="block text-xs font-bold uppercase tracking-widest opacity-60 mb-2">
          {t.support.messageLabel}
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={!hasUsername || submitting}
          maxLength={MAX_MESSAGE_LENGTH}
          rows={6}
          placeholder={t.support.messagePlaceholder}
          className="w-full rounded-2xl p-4 text-base resize-none outline-none"
          style={{
            backgroundColor: theme.cardBgLight,
            border: `1px solid ${theme.cardBorder}`,
            color: theme.text.primary,
            opacity: hasUsername ? 1 : 0.55,
          }}
        />
        <div className="mt-1 text-xs opacity-50 text-right">
          {message.trim().length}/{MAX_MESSAGE_LENGTH}
        </div>
      </section>

      <Button
        variant="primary"
        fullWidth
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        {submitting ? t.support.submitting : t.support.submit}
      </Button>
    </div>
  )
}
